import { createTrashPanicZeroGame } from "@game/TrashPanicZeroGame.js";
import type {
  CompleteRunResponse,
  GameContext,
  LiveStatus,
  PresentationSettings,
  QualityLevel,
  RunSummary,
  ShuvGame,
} from "./types.js";
import { InputManager } from "./input.js";
import { loadSlot, saveSlot } from "./save.js";
import { recordRun, recordSimpleRun } from "./scores.js";
import type { RunBundle } from "@game/simulation/types.js";

export type BootHandlers = {
  onProgress?: (label: string, ratio: number) => void;
  onRunEnd?: (summary: RunSummary, result: CompleteRunResponse) => void;
  onTelemetry?: (event: string, data?: Record<string, unknown>) => void;
};

/** Persisted settings shape (LOCKED-DESIGN 18.5 settings surface). */
export type SessionSettings = {
  quality: QualityLevel;
  qualityAuto: boolean;
  muted: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  screenshakeIntensity: number;
  flashReduction: boolean;
  /**
   * Simple-only comfort valve (LOCKED-DESIGN 18.15 / PLAYTEST F4). When true a
   * SIMPLE run is created with the kernel's `jamsDisabled` option so the furnace
   * never gets stuck. Ignored outside Simple — the Engine only forwards it on a
   * Simple run and the kernel independently refuses it on ranked seeds, so a
   * persisted `true` can never leak into ranked play. Default false.
   */
  simpleJamsDisabled: boolean;
};

export type GameSession = {
  game: ShuvGame;
  context: GameContext;
  input: InputManager;
  start: () => void;
  pause: () => void;
  resume: () => void;
  restart: () => void;
  destroy: () => void;
  resize: () => void;
  configureRun: (seedId: string, practicePhase: number) => void;
  startPrologue: () => void;
  getRunBundle: () => RunBundle | null;
  /**
   * Current presentation mode (LOCKED-DESIGN 18.11). Live-read from the game
   * context; the shell uses it to render the Simple vs adult surfaces.
   */
  readonly mode: "standard" | "simple";
  /**
   * Select and persist the presentation mode (LOCKED-DESIGN 18.11). Writes the
   * "mode" save slot ({ simple }) so the choice is restored on next launch.
   * Does NOT touch the Simulation — Simple honesty rides on seed.ruleset.
   */
  setMode: (mode: "standard" | "simple") => void;
  primeAudio: () => void;
  isTutorialActive: () => boolean;
  getLiveStatus: () => LiveStatus | null;
  getFps: () => number;
  /** Show/hide the in-arena HUD (LOCKED-DESIGN 18.10 residual). */
  setHudVisible: (visible: boolean) => void;
  /** live settings snapshot + persisting setters for the shell settings panel */
  settings: SessionSettings;
  setQuality: (level: QualityLevel, auto: boolean) => void;
  setMuted: (muted: boolean) => void;
  setMasterVolume: (v: number) => void;
  setMusicVolume: (v: number) => void;
  setSfxVolume: (v: number) => void;
  setScreenshakeIntensity: (v: number) => void;
  setFlashReduction: (reduced: boolean) => void;
  /**
   * Simple-only jam toggle (LOCKED-DESIGN 18.15 / F4). Persists into the existing
   * settings slot and updates the live context; the Engine reads the context ONCE
   * at run creation, so flipping this mid-run never perturbs the live stream — it
   * applies to the next shift (the settings note says exactly that).
   */
  setJamsDisabled: (disabled: boolean) => void;
};

const SETTINGS_SLOT = "settings";
const MODE_SLOT = "mode";
const VALID_QUALITY: QualityLevel[] = ["potato", "low", "medium", "high"];

export async function bootGame(
  container: HTMLElement,
  canvas: HTMLCanvasElement,
  handlers: BootHandlers = {},
): Promise<GameSession> {
  const input = new InputManager(container);

  // Persisted settings (LOCKED-DESIGN 18.5 / 12.3): loaded through the existing
  // save layer. prefers-reduced-motion pre-checks flash reduction on a fresh
  // profile so first launch already respects the OS accessibility preference.
  const prefersReducedMotion =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saved = await loadSlot(SETTINGS_SLOT);
  const legacyPresentation = await loadSlot("presentation");
  // Presentation mode (LOCKED-DESIGN 18.11): default "standard" on fresh
  // storage so the adult prologue flow (and qa:arena's beforeEach) is untouched.
  const savedMode = await loadSlot(MODE_SLOT);
  const initialMode: "standard" | "simple" = savedMode?.["simple"] === true ? "simple" : "standard";
  const settings: SessionSettings = {
    quality: readQuality(saved?.["quality"]),
    qualityAuto: saved?.["qualityAuto"] === true,
    muted: saved?.["muted"] === true,
    masterVolume: clamp01(readNumber(saved?.["masterVolume"], 1)),
    musicVolume: clamp01(readNumber(saved?.["musicVolume"], 0.6)),
    sfxVolume: clamp01(readNumber(saved?.["sfxVolume"], 0.85)),
    screenshakeIntensity: clamp01(
      readNumber(saved?.["screenshakeIntensity"] ?? legacyPresentation?.["screenshakeIntensity"], 1),
    ),
    flashReduction:
      saved?.["flashReduction"] === true ||
      legacyPresentation?.["flashReduction"] === true ||
      (saved === null && legacyPresentation === null && prefersReducedMotion),
    // F4 (18.15): opt-in only — a fresh profile keeps jams ON, so the default
    // Simple experience is unchanged and nothing silently disables a mechanic.
    simpleJamsDisabled: saved?.["simpleJamsDisabled"] === true,
  };

  // Presentation-only knobs (12.3/18.5): the game keeps a live reference; the
  // shell settings panel edits them through the setters below.
  const presentation: PresentationSettings = {
    screenshakeIntensity: settings.screenshakeIntensity,
    flashReduction: settings.flashReduction,
  };

  const persist = (): void => {
    void saveSlot(SETTINGS_SLOT, { ...settings });
  };

  const endRun = async (summary: RunSummary): Promise<CompleteRunResponse> => {
    // Simple runs are unranked (LOCKED-DESIGN 18.11): route them to the kid
    // best slot so they never clobber the adult best labels or history.
    const simple = context.mode === "simple";
    const { personalBest, best } = simple ? recordSimpleRun(summary) : recordRun(summary);
    const score = summary.metrics.find((m) => m.leaderboardId === "score")?.value ?? 0;
    const combo = summary.metrics.find((m) => m.leaderboardId === "combo")?.value ?? 0;
    const response: CompleteRunResponse = {
      runId: `local-${Date.now()}`,
      state: "accepted",
      metrics: [
        { leaderboardId: "score", value: score, personalBest },
        { leaderboardId: "combo", value: combo },
      ],
      // "New best" only when the fix in scores.ts confirms a real (>0) best —
      // a zero-score or failed first run never reads as celebration (18.5/18.8).
      // Simple's own results hero renders the kid best line; this stays warm.
      message: personalBest
        ? simple
          ? `New best · ${best.score.toLocaleString()}!`
          : `New facility best · ${best.score.toLocaleString()}`
        : undefined,
    };
    handlers.onRunEnd?.(summary, response);
    return response;
  };

  const context: GameContext = {
    canvas,
    container,
    quality: settings.quality,
    muted: settings.muted,
    masterVolume: settings.masterVolume,
    musicVolume: settings.musicVolume,
    sfxVolume: settings.sfxVolume,
    presentation,
    mode: initialMode,
    simpleJamsDisabled: settings.simpleJamsDisabled,
    getInput: () => input.snapshot(),
    submitScore: async () => ({ accepted: true }),
    unlockMedal: async (medalId) => ({
      medalId,
      unlockedAt: new Date().toISOString(),
    }),
    save: saveSlot,
    load: loadSlot,
    reportTelemetry: (event, data) => {
      handlers.onTelemetry?.(event, data);
    },
    setQuality: (level) => {
      settings.quality = level;
      context.quality = level;
      game.setQuality?.(level);
    },
    setMuted: (m) => {
      settings.muted = m;
      context.muted = m;
    },
    setMasterVolume: (v) => {
      settings.masterVolume = clamp01(v);
      context.masterVolume = settings.masterVolume;
    },
    setMusicVolume: (v) => {
      settings.musicVolume = clamp01(v);
      context.musicVolume = settings.musicVolume;
    },
    setSfxVolume: (v) => {
      settings.sfxVolume = clamp01(v);
      context.sfxVolume = settings.sfxVolume;
    },
    setScreenshakeIntensity: (intensity) => {
      presentation.screenshakeIntensity = clamp01(intensity);
      settings.screenshakeIntensity = presentation.screenshakeIntensity;
    },
    setFlashReduction: (reduced) => {
      presentation.flashReduction = reduced;
      settings.flashReduction = reduced;
    },
    endRun,
    reportProgress: (p) => {
      const ratio = p.total > 0 ? p.loaded / p.total : 0;
      handlers.onProgress?.(p.label ?? p.phase, ratio);
    },
  };

  const game = createTrashPanicZeroGame(context);
  await game.preload(context);
  game.mount(context);

  const resize = () => {
    const width = container.clientWidth || 1;
    const height = container.clientHeight || 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    game.resize?.({ width, height, dpr });
  };

  resize();

  return {
    game,
    context,
    input,
    start: () => {
      input.resume();
      game.start();
      container.focus();
    },
    pause: () => {
      game.pause();
      input.pause();
    },
    resume: () => {
      input.resume();
      game.resume();
      container.focus();
    },
    restart: () => {
      input.resume();
      game.restart();
      game.start();
      container.focus();
    },
    destroy: () => {
      void game.destroy();
      input.destroy();
    },
    resize,
    configureRun: (seedId, practicePhase) => game.configureRun?.(seedId, practicePhase),
    startPrologue: () => game.startPrologue?.(),
    getRunBundle: () => (game.getRunBundle?.() as RunBundle | undefined) ?? null,
    get mode() {
      return context.mode ?? "standard";
    },
    setMode: (mode) => {
      context.mode = mode;
      void saveSlot(MODE_SLOT, { simple: mode === "simple" });
    },
    primeAudio: () => game.primeAudio?.(),
    isTutorialActive: () => game.isTutorialActive?.() ?? false,
    getLiveStatus: () => game.getLiveStatus?.() ?? null,
    getFps: () => game.getFps?.() ?? 0,
    setHudVisible: (visible) => game.setHudVisible?.(visible),
    settings,
    setQuality: (level, auto) => {
      settings.qualityAuto = auto;
      context.setQuality(level);
      persist();
    },
    setMuted: (m) => {
      context.setMuted(m);
      persist();
    },
    setMasterVolume: (v) => {
      context.setMasterVolume(v);
      persist();
    },
    setMusicVolume: (v) => {
      context.setMusicVolume(v);
      persist();
    },
    setSfxVolume: (v) => {
      context.setSfxVolume(v);
      persist();
    },
    setScreenshakeIntensity: (v) => {
      context.setScreenshakeIntensity(v);
      persist();
    },
    setFlashReduction: (reduced) => {
      context.setFlashReduction(reduced);
      persist();
    },
    setJamsDisabled: (disabled) => {
      settings.simpleJamsDisabled = disabled;
      context.simpleJamsDisabled = disabled;
      persist();
    },
  };
}

function readQuality(value: unknown): QualityLevel {
  return typeof value === "string" && VALID_QUALITY.includes(value as QualityLevel)
    ? (value as QualityLevel)
    : "high";
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 1;
}
